import { type DesktopRelease, GET_PATH, INSTALL_PATH, inWords, systemsIn } from './desktop-app';

/**
 * The guides, as a list: one per job, named for the person doing it
 * (`docs/off-board-plan.md` §9.3).
 *
 * This is the index and nothing else — a guide's address, who it is for, what it is called, and
 * whether it is about the desktop app. The words of each guide are in `pages/Guides.tsx`, so a
 * page that only links to a guide (the front door, the footer) does not carry any of them.
 *
 * **A guide about the app does not exist until the app does.** `needsApp` guides are left out of
 * the index and answer as "no such guide" while there is no release (`desktop-app.ts`); the rest
 * are true today and say more once there is one, which is why a title and a summary are functions
 * of the release and not strings.
 *
 * Four entries are pages that were here before there was an index — `/install`, `/start`, `/mcp`
 * and `/downloads` — and one is `/get`. They keep their addresses, which are linked from out in the
 * world; the index points at them and `/guides/<slug>` never shadows one.
 */

export const GUIDES_PATH = '/guides';

export type GuideAudience = 'players' | 'creators';

export interface Guide {
  /** The guide's name in an address and in the code. */
  slug: string;
  /** Where it is read: `/guides/<slug>`, or the page that was already there. */
  href: string;
  audience: GuideAudience;
  /** About the desktop app from its first word: neither listed nor reachable without a release. */
  needsApp: boolean;
  /**
   * Read at its address and never listed on the index: a guide to something a team may not have,
   * linked from the place a team that has it already is (CLAUDE.md, plans are invisible).
   */
  unlisted?: boolean;
  /**
   * About the install with no app. With a released app that is a way only an account that was
   * given it has (`docs/library-first-plan.md` §6), so it is listed for that account and for
   * nobody else; with no app it is how a game is installed, and is listed for everybody.
   */
  byHand?: boolean;
  title: (app: DesktopRelease | null) => string;
  /** One sentence, for the index. */
  summary: (app: DesktopRelease | null) => string;
}

const at = (slug: string) => `${GUIDES_PATH}/${slug}`;

export const GUIDES: readonly Guide[] = [
  {
    slug: 'get',
    href: GET_PATH,
    audience: 'players',
    needsApp: true,
    title: () => 'Get BoardFlight for your computer',
    summary: (app) => (app ? `The download, for ${inWords(systemsIn(app).map((system) => system.name))}.` : 'The download.'),
  },
  {
    slug: 'set-up',
    href: at('set-up'),
    audience: 'players',
    needsApp: true,
    title: () => 'Set up BoardFlight',
    summary: () => 'Get the app, sign in, pair your Board and install a first game.',
  },
  {
    slug: 'install',
    href: INSTALL_PATH,
    audience: 'players',
    needsApp: false,
    byHand: true,
    title: (app) => (app ? 'Install a game without the app' : 'Install a game on your Board'),
    summary: () => 'Download a game’s file, then drop it on your Board’s own page.',
  },
  {
    slug: 'phone',
    href: at('phone'),
    audience: 'players',
    needsApp: true,
    title: () => 'Install from your phone',
    summary: () => 'Pick a game on your phone, and what “the next time BoardFlight is open” means.',
  },
  {
    slug: 'updates',
    href: at('updates'),
    audience: 'players',
    needsApp: false,
    title: () => 'Keep games up to date',
    summary: (app) =>
      app ? 'Update, Update all, automatic updates, and the card a game shows.' : 'How a newer version of a game gets onto your Board.',
  },
  {
    slug: 'redeem',
    href: at('redeem'),
    audience: 'players',
    needsApp: false,
    title: () => 'Redeem a code',
    summary: () => 'Somebody gave you a code for a game. What to do with it.',
  },
  {
    slug: 'help',
    href: at('help'),
    audience: 'players',
    needsApp: false,
    title: () => 'When it does not work',
    summary: (app) =>
      app
        ? 'No Board found, a pairing nobody answered, an install that did not go through.'
        : 'The Board’s page will not open, the code is not taken, the file is not accepted.',
  },
  {
    slug: 'changes',
    href: at('changes'),
    audience: 'players',
    needsApp: false,
    title: () => 'What changed',
    summary: () => 'For anybody who had the BoardFlight app on their Board.',
  },
  {
    slug: 'make-a-game',
    href: at('make-a-game'),
    audience: 'creators',
    needsApp: false,
    title: () => 'Make a game for Board',
    summary: () => 'The whole path: build it in a browser or an engine, then get it onto other people’s Boards.',
  },
  {
    slug: 'start',
    href: '/start',
    audience: 'creators',
    needsApp: false,
    title: () => 'Publish a build',
    summary: () => 'From a build on your computer to a tester who has it, in seven steps.',
  },
  {
    slug: 'testers',
    href: at('testers'),
    audience: 'creators',
    needsApp: false,
    title: () => 'How your testers install',
    summary: () => 'What you send them, what they are asked to do, and what you see of it.',
  },
  {
    slug: 'your-board',
    href: at('your-board'),
    audience: 'creators',
    needsApp: true,
    title: () => 'Publish and see it on your own Board',
    summary: () => 'Automatic updates, so a build you publish reaches your Board by itself.',
  },
  {
    slug: 'versions',
    href: at('versions'),
    audience: 'creators',
    needsApp: false,
    title: () => 'Show your version and offer updates',
    summary: () => 'The update card, and asking BoardFlight which build your game is.',
  },
  {
    slug: 'achievements',
    href: at('achievements'),
    audience: 'creators',
    needsApp: false,
    // Reached from a channel's Achievements tab and from the API's own description — never from the index,
    // which every creator reads (`docs/achievements-plan.md` §10.3).
    unlisted: true,
    title: () => 'Achievements',
    summary: () => 'Declare them, fire them from your game, and read who has them.',
  },
  {
    slug: 'unity',
    href: '/downloads',
    audience: 'creators',
    needsApp: false,
    title: () => 'The Unity plugin',
    summary: () => 'Build and upload from the editor.',
  },
  {
    slug: 'agents',
    href: '/mcp',
    audience: 'creators',
    needsApp: false,
    title: () => 'Connect your AI agent',
    summary: () => 'Let the agent in your own editor see your games, their builds and what your testers said.',
  },
];

/**
 * The guides the index lists: with a release, all of them but the install by hand, which is for
 * an account that has it (`byHand`); without one, only today's — which is the install by hand.
 * Never an `unlisted` one, which is read only where it is linked from.
 */
export function guidesFor(app: DesktopRelease | null, byHand = false): Guide[] {
  return GUIDES.filter((guide) => !guide.unlisted && (app !== null ? byHand || !guide.byHand : !guide.needsApp));
}

/** A slug as it would be written in `/guides/<slug>`, or null for any other path. */
export function guideSlug(path: string): string | null {
  const prefix = `${GUIDES_PATH}/`;
  if (!path.startsWith(prefix)) return null;
  const slug = path.slice(prefix.length).replace(/\/+$/, '');
  return slug === '' || slug.includes('/') ? null : slug;
}

/** Whether a path is the index or one of its guides — the route's question (`App.tsx`). */
export function isGuidesPath(path: string): boolean {
  return path === GUIDES_PATH || path === `${GUIDES_PATH}/` || path.startsWith(`${GUIDES_PATH}/`);
}

export type GuideLookup =
  /** A guide that is read here, today. */
  | { kind: 'guide'; guide: Guide }
  /** A guide about the app, asked for before anybody knows whether there is one. */
  | { kind: 'waiting'; guide: Guide }
  /** Nothing by that name — or a guide about an app that is not out, which is the same answer. */
  | { kind: 'missing' };

/**
 * The guide at `/guides/<slug>`. Only a guide whose page *is* `/guides/<slug>`: the ones that live
 * at an address of their own are reached there.
 *
 * `settled` is whether the release has been asked for and answered. Until it has, a guide about
 * the app is neither shown nor refused — refusing it and then showing it a moment later would be
 * the one flash of "there is no such thing" this gate exists to avoid in the other direction.
 */
export function findGuide(slug: string | null, app: DesktopRelease | null, settled: boolean = true): GuideLookup {
  const guide = slug === null ? undefined : GUIDES.find((candidate) => candidate.slug === slug && candidate.href === at(slug));
  if (!guide) return { kind: 'missing' };
  if (!guide.needsApp || app !== null) return { kind: 'guide', guide };
  return settled ? { kind: 'missing' } : { kind: 'waiting', guide };
}

/** `/guides/<slug>` for a guide that lives there. For links from other pages. */
export const guideHref = at;
